import type { WidgetDto } from '@asset-metrix/portal-gateway-client-js';
import { v4 as uuidV4 } from 'uuid';
import { create } from 'zustand';

import {
  type DashboardFilterSelections,
  storedFilterSelections,
} from '../../components/customizableDashboard/dashboardTileFilters';
import type {
  DashboardUpdateRequest,
  DraftDashboardTile,
  HydratedDashboard,
  HydratedDashboardContent,
} from '../../components/customizableDashboard/utils/dashboard.types';
import {
  areDashboardUpdateRequestsEqual,
  projectDashboardUpdateRequest,
  toDashboardContentPlacement,
} from '../../components/customizableDashboard/utils/dashboardUpdateHelpers';

type AppendPlacementInput = {
  instanceId: string;
  widthFactor: number;
  content: WidgetDto;
  sourcePageId: string;
};

type PendingDashboardModal =
  'createDashboard' | 'allDashboards' | 'addComponents';

type DashboardEditModeState = {
  editDashboardId: string | null;
  draftContents: DraftDashboardTile[] | null;
  baselineForDirtyCheck: DashboardUpdateRequest | null;
  draftMetadata: Pick<
    HydratedDashboard,
    'sourcePageId' | 'name' | 'description' | 'sharingStatus'
  > | null;
  isSaving: boolean;
  /** Tiles whose filter options are still loading. Save stays disabled while any remain. */
  tileFiltersPendingCounts: Record<string, number>;
  /** One-shot: after create, open the new dashboard already in edit mode. */
  pendingOpenInEditDashboardId: string | null;
  /** State to open Add Components, Create Dashboard, and All Dashboards modals from the root. */
  pendingDashboardModal: PendingDashboardModal | null;
  enterDashboardEdit: (
    dashboardId: string,
    hydrated: HydratedDashboard
  ) => void;
  exitDashboardEdit: () => void;
  requestOpenInEdit: (dashboardId: string) => void;
  clearPendingOpenInEdit: () => void;
  requestOpenDashboardModal: (modal: PendingDashboardModal) => void;
  clearPendingDashboardModal: () => void;
  moveContent: (instanceId: string, direction: 'up' | 'down') => void;
  toggleContentWidth: (instanceId: string) => void;
  duplicateContent: (
    instanceId: string,
    customFilterColumnIds?: string[]
  ) => string | null;
  replaceDraftCustomFilterColumnIds: (
    instanceId: string,
    columnIds: string[]
  ) => boolean;
  removeContent: (instanceId: string) => void;
  appendPlacement: (placement: AppendPlacementInput) => void;
  updateTileFilterSelections: (
    instanceId: string,
    selections: {
      pageFilterSelections?: DashboardFilterSelections;
      customTableFilterSelections?: DashboardFilterSelections;
    }
  ) => void;
  setIsSaving: (isSaving: boolean) => void;
  adjustTileFiltersPending: (instanceId: string, delta: 1 | -1) => void;
};

function updateDraft(
  state: DashboardEditModeState,
  update: (draft: DraftDashboardTile[]) => DraftDashboardTile[]
): Partial<DashboardEditModeState> {
  // Save already snapped the payload; refuse further draft edits until it settles.
  if (state.draftContents == null || state.isSaving) {
    return {};
  }
  return { draftContents: update(state.draftContents) };
}

export const useDashboardEditModeStore = create<DashboardEditModeState>(
  (set, get) => ({
    editDashboardId: null,
    draftContents: null,
    baselineForDirtyCheck: null,
    draftMetadata: null,
    isSaving: false,
    tileFiltersPendingCounts: {},
    pendingOpenInEditDashboardId: null,
    pendingDashboardModal: null,
    enterDashboardEdit: (dashboardId, hydrated) => {
      const draftContents: DraftDashboardTile[] = hydrated.contents.map(
        (tile) => structuredClone(tile)
      );
      const metadata = {
        sourcePageId: hydrated.sourcePageId,
        name: hydrated.name,
        description: hydrated.description ?? null,
        sharingStatus: hydrated.sharingStatus,
      };
      set({
        editDashboardId: dashboardId,
        draftContents,
        draftMetadata: metadata,
        baselineForDirtyCheck: projectDashboardUpdateRequest(
          metadata,
          draftContents
        ),
        isSaving: false,
        tileFiltersPendingCounts: {},
      });
    },
    exitDashboardEdit: () =>
      set({
        editDashboardId: null,
        draftContents: null,
        baselineForDirtyCheck: null,
        draftMetadata: null,
        isSaving: false,
        tileFiltersPendingCounts: {},
        pendingDashboardModal: null,
      }),
    requestOpenInEdit: (dashboardId) =>
      set({ pendingOpenInEditDashboardId: dashboardId }),
    clearPendingOpenInEdit: () => set({ pendingOpenInEditDashboardId: null }),
    requestOpenDashboardModal: (modal) => set({ pendingDashboardModal: modal }),
    clearPendingDashboardModal: () => set({ pendingDashboardModal: null }),
    moveContent: (instanceId, direction) =>
      set((state) =>
        updateDraft(state, (draft) => {
          const index = draft.findIndex(
            (item) => item.instanceId === instanceId
          );
          if (index === -1) {
            return draft;
          }
          const nextIndex = direction === 'up' ? index - 1 : index + 1;
          if (nextIndex < 0 || nextIndex >= draft.length) {
            return draft;
          }
          const next = [...draft];
          const current = next[index];
          const neighbor = next[nextIndex];
          if (current == null || neighbor == null) {
            return draft;
          }
          next[index] = neighbor;
          next[nextIndex] = current;
          return next;
        })
      ),
    toggleContentWidth: (instanceId) =>
      set((state) =>
        updateDraft(state, (draft) =>
          draft.map((item) =>
            item.instanceId !== instanceId
              ? item
              : {
                  ...item,
                  widthFactor: item.widthFactor <= 0.5 ? 1 : 0.5,
                }
          )
        )
      ),
    duplicateContent: (instanceId, customFilterColumnIds) => {
      const newInstanceId = uuidV4();
      let created = false;
      set((state) =>
        updateDraft(state, (draft) => {
          const index = draft.findIndex(
            (item) => item.instanceId === instanceId
          );
          if (index === -1) {
            return draft;
          }
          const source = draft[index];
          if (source == null) {
            return draft;
          }
          created = true;
          const clone = structuredClone(source);
          const duplicated: DraftDashboardTile = {
            ...clone,
            instanceId: newInstanceId,
            ...(customFilterColumnIds == null
              ? {}
              : { draftCustomFilterColumnIds: customFilterColumnIds }),
          };
          const next = [...draft];
          next.splice(index + 1, 0, duplicated);
          return next;
        })
      );
      return created ? newInstanceId : null;
    },
    replaceDraftCustomFilterColumnIds: (instanceId, columnIds) => {
      const state = get();
      const tile = state.draftContents?.find(
        (item) => item.instanceId === instanceId
      );
      if (tile == null) {
        return false;
      }
      // Still draft-managed (skip API write), but do not change the save payload.
      if (state.isSaving) {
        return true;
      }
      set((current) =>
        updateDraft(current, (draft) =>
          draft.map((item) =>
            item.instanceId === instanceId
              ? { ...item, draftCustomFilterColumnIds: columnIds }
              : item
          )
        )
      );
      return true;
    },
    removeContent: (instanceId) =>
      set((state) =>
        updateDraft(state, (draft) =>
          draft.filter((item) => item.instanceId !== instanceId)
        )
      ),
    appendPlacement: ({ instanceId, widthFactor, content, sourcePageId }) =>
      set((state) =>
        updateDraft(state, (draft) => [
          ...draft,
          {
            instanceId,
            contentId: content.id,
            widthFactor,
            sourcePageId,
            content,
            draftCustomFilterColumnIds: [],
          },
        ])
      ),
    updateTileFilterSelections: (instanceId, selections) =>
      set((state) =>
        updateDraft(state, (draft) =>
          draft.map((tile) =>
            tile.instanceId !== instanceId
              ? tile
              : {
                  ...tile,
                  pageFilterSelections: undefined,
                  customTableFilterSelections: undefined,
                  ...storedFilterSelections(selections),
                }
          )
        )
      ),
    setIsSaving: (isSaving) => set({ isSaving }),
    adjustTileFiltersPending: (instanceId, delta) =>
      set((state) => {
        const next = (state.tileFiltersPendingCounts[instanceId] ?? 0) + delta;
        const tileFiltersPendingCounts = { ...state.tileFiltersPendingCounts };
        if (next <= 0) {
          delete tileFiltersPendingCounts[instanceId];
        } else {
          tileFiltersPendingCounts[instanceId] = next;
        }
        return { tileFiltersPendingCounts };
      }),
  })
);

export function useDraftCustomFilterColumnIds(
  dashboardId: string | null | undefined,
  instanceId: string | undefined
): string[] | undefined {
  return useDashboardEditModeStore((state) => {
    if (
      dashboardId == null ||
      instanceId == null ||
      state.editDashboardId !== dashboardId
    ) {
      return undefined;
    }
    return state.draftContents?.find((tile) => tile.instanceId === instanceId)
      ?.draftCustomFilterColumnIds;
  });
}

export function useIsDashboardDraftMode(
  dashboardId: string | null | undefined
): boolean {
  return useDashboardEditModeStore(
    (state) =>
      dashboardId != null &&
      state.editDashboardId === dashboardId &&
      state.draftContents != null
  );
}

export function useIsDashboardDraftDirty(
  dashboardId: string | null | undefined
): boolean {
  return useDashboardEditModeStore((state) => {
    if (
      dashboardId == null ||
      state.editDashboardId !== dashboardId ||
      state.draftContents == null ||
      state.draftMetadata == null ||
      state.baselineForDirtyCheck == null
    ) {
      return false;
    }
    if (
      state.draftContents.some(
        (tile) => tile.draftCustomFilterColumnIds != null
      )
    ) {
      return true;
    }
    const projected = projectDashboardUpdateRequest(
      state.draftMetadata,
      state.draftContents
    );
    return !areDashboardUpdateRequestsEqual(
      projected,
      state.baselineForDirtyCheck
    );
  });
}

export function getDashboardDraftMoveAvailability(
  tiles: readonly DraftDashboardTile[],
  instanceId: string
): { canMoveUp: boolean; canMoveDown: boolean } {
  const index = tiles.findIndex((item) => item.instanceId === instanceId);
  if (index === -1) {
    return { canMoveUp: false, canMoveDown: false };
  }
  return {
    canMoveUp: index > 0,
    canMoveDown: index < tiles.length - 1,
  };
}

export function mapHydratedToDraftTiles(
  contents: HydratedDashboardContent[]
): DraftDashboardTile[] {
  return contents.map((tile) => structuredClone(tile));
}

export { projectDashboardUpdateRequest, toDashboardContentPlacement };
